/**
 * TaskCard Component
 * 
 * Displays individual actionable tasks in the Task Dashboard.
 * Follows "Task-First, Not Feature-First" UX pattern.
 * 
 * @see UX_ACTION_PLAN_ENHANCED_2025-10-13.md
 */

import { Task } from '@/hooks/useTaskGeneration';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

interface TaskCardProps {
  task: Task;
  onClick?: () => void;
}

// Priority color mapping - Swiss OS Design System
const PRIORITY_COLORS = {
  high: {
    bg: 'bg-validation-critical-bg',
    border: 'border-validation-critical',
    text: 'text-validation-critical',
    badge: 'bg-brand-danger-bg text-brand-danger hover:bg-brand-danger-bg/80',
  },
  medium: {
    bg: 'bg-validation-high-bg',
    border: 'border-validation-high',
    text: 'text-validation-high',
    badge: 'bg-brand-warning-bg text-brand-warning hover:bg-brand-warning-bg/80',
  },
  low: {
    bg: 'bg-validation-low-bg',
    border: 'border-validation-low',
    text: 'text-validation-low',
    badge: 'bg-brand-success-bg text-brand-success hover:bg-brand-success-bg/80',
  },
};

// Status icon mapping
const STATUS_ICONS = {
  pending: '⏳',
  in_progress: '▶️',
  completed: '✅',
};

export const TaskCard = ({ task, onClick }: TaskCardProps) => {
  const colors = PRIORITY_COLORS[task.priority];
  const statusIcon = STATUS_ICONS[task.status];
  const isCompleted = task.status === 'completed';
  const isDisabled = isCompleted;

  return (
    <Card 
      className={`
        ${colors.bg} 
        ${colors.border} 
        border-l-4 
        rounded-2xl
        transition-all 
        hover:shadow-[var(--shadow-elegant)]
        ${isDisabled ? 'opacity-60' : 'cursor-pointer hover:scale-[1.01]'}
        ${isCompleted ? 'border-l-brand-success' : ''}
      `}
      onClick={!isDisabled ? (onClick || task.action) : undefined}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 flex-1">
            {/* Task Icon */}
            <div className="text-3xl flex-shrink-0">
              {task.icon}
            </div>

            {/* Task Title & Description */}
            <div className="flex-1 min-w-0">
              <CardTitle className={`text-lg font-semibold font-space-grotesk ${colors.text} flex items-center gap-2`}>
                <span>{task.title}</span>
                {task.count && (
                  <Badge variant="secondary" className={colors.badge}>
                    {task.count}
                  </Badge>
                )}
              </CardTitle>
              <CardDescription className="mt-1 text-sm text-brand-text-secondary">
                {task.description}
              </CardDescription>
            </div>
          </div>

          {/* Priority Badge */}
          <Badge 
            variant="outline"
            className={`${colors.badge} flex-shrink-0 uppercase text-xs`}
          >
            {task.priority}
          </Badge>
        </div>
      </CardHeader>

      <CardContent>
        <div className="flex items-center justify-between gap-4">
          {/* Metadata */}
          <div className="flex items-center gap-4 text-sm text-brand-text-secondary">
            {/* Estimated Time */}
            {task.estimatedTime && (
              <div className="flex items-center gap-1">
                <i className="fa-solid fa-clock"></i>
                <span>{task.estimatedTime}</span>
              </div>
            )}

            {/* Status */}
            <div className="flex items-center gap-1">
              <span>{statusIcon}</span>
              <span className="capitalize">{task.status.replace('_', ' ')}</span>
            </div>

            {/* Last Update */}
            {task.metadata?.lastUpdate && (
              <span className="text-xs">
                Last: {task.metadata.lastUpdate}
              </span>
            )}
          </div>

          {/* Action Button */}
          {!isCompleted && (
            <Button 
              size="sm"
              variant={task.priority === 'high' ? 'default' : 'outline'}
              onClick={(e) => {
                e.stopPropagation();
                if (onClick) {
                  onClick();
                } else {
                  task.action();
                }
              }}
              className="flex items-center gap-2 rounded-lg"
            >
              Start
              <i className="fa-solid fa-arrow-right"></i>
            </Button>
          )}

          {isCompleted && (
            <div className="text-brand-success font-medium flex items-center gap-1">
              <i className="fa-solid fa-check-circle"></i>
              <span>Done</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
